/**
 * CreationManager - Handles element creation modes and click-drag creation
 */

import Konva from 'konva';
import { EventEmitter } from '../core/EventEmitter';
import { Point } from '../core/types';
import { EditableElement, CreateMode, ShapeType } from '../editing/types';
import {
  createStickyNote,
  createText,
  createShape,
  createOrganizer,
  createSection,
  createElementFromBounds,
  createElementAtPoint,
} from '../editing/ElementFactory';

export interface CreationState {
  mode: CreateMode;
  isCreating: boolean;
  startPoint: Point | null;
  currentPoint: Point | null;
  previewElement: EditableElement | null;
}

export interface CreationManagerOptions {
  minSize?: number;
  snapToGrid?: boolean;
  gridSize?: number;
}

const DEFAULT_OPTIONS: Required<CreationManagerOptions> = {
  minSize: 20,
  snapToGrid: false,
  gridSize: 10,
};

export class CreationManager {
  private layer: Konva.Layer;
  private emitter: EventEmitter;
  private options: Required<CreationManagerOptions>;
  
  // Creation state
  private state: CreationState = {
    mode: { type: 'select' },
    isCreating: false,
    startPoint: null,
    currentPoint: null,
    previewElement: null,
  };
  
  // Preview node (for drag operations)
  private previewNode: Konva.Group | null = null;
  
  // Cursor preview node (for tool selection)
  private cursorPreviewNode: Konva.Group | null = null;
  
  // Callbacks
  private onElementCreated: ((element: EditableElement) => void) | null = null;

  constructor(
    layer: Konva.Layer,
    emitter: EventEmitter,
    options: CreationManagerOptions = {}
  ) {
    this.layer = layer;
    this.emitter = emitter;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Set creation callback
   */
  setOnElementCreated(callback: (element: EditableElement) => void): void {
    this.onElementCreated = callback;
  }

  /**
   * Set current creation mode
   */
  setMode(mode: CreateMode): void {
    this.state = {
      mode,
      isCreating: false,
      startPoint: null,
      currentPoint: null,
      previewElement: null,
    };
    this.clearPreview();
    this.hideCursorPreview();
    
    // Note: Cursor preview will be shown when mouse moves over canvas
    // or can be triggered manually via showCursorPreview()
  }

  /**
   * Get current mode
   */
  getMode(): CreateMode {
    return this.state.mode;
  }

  /**
   * Check if in create mode (not select or pan)
   */
  isCreateMode(): boolean {
    return this.state.mode.type !== 'select' && this.state.mode.type !== 'pan';
  }

  /**
   * Check if in select mode
   */
  isSelectMode(): boolean {
    return this.state.mode.type === 'select';
  }

  /**
   * Check if in pan mode
   */
  isPanMode(): boolean {
    return this.state.mode.type === 'pan';
  }

  /**
   * Check if currently creating
   */
  isCreating(): boolean {
    return this.state.isCreating;
  }

  /**
   * Snap coordinate to grid
   */
  private snapToGridValue(value: number): number {
    if (!this.options.snapToGrid) return value;
    return Math.round(value / this.options.gridSize) * this.options.gridSize;
  }

  /**
   * Start creating an element (mouse down)
   */
  startCreating(x: number, y: number): void {
    if (this.state.mode.type === 'select' || this.state.mode.type === 'pan') return;
    
    const snappedX = this.snapToGridValue(x);
    const snappedY = this.snapToGridValue(y);
    
    // Hide cursor preview when starting drag
    this.hideCursorPreview();
    
    this.state = {
      ...this.state,
      isCreating: true,
      startPoint: { x: snappedX, y: snappedY },
      currentPoint: { x: snappedX, y: snappedY },
      previewElement: null,
    };
  }

  /**
   * Update creation preview (mouse move)
   */
  updateCreating(x: number, y: number): void {
    if (!this.state.isCreating || !this.state.startPoint) return;
    
    // Store startPoint in local variable to avoid null check issues after state update
    const startPoint = this.state.startPoint;
    
    const snappedX = this.snapToGridValue(x);
    const snappedY = this.snapToGridValue(y);
    
    // Update current point even if we don't show preview yet (for click detection)
    this.state = {
      ...this.state,
      currentPoint: { x: snappedX, y: snappedY },
    };
    
    // Calculate distance to determine if user is dragging
    const deltaX = Math.abs(snappedX - startPoint.x);
    const deltaY = Math.abs(snappedY - startPoint.y);
    const isDragging = deltaX > 2 || deltaY > 2; // Small threshold to detect drag
    
    if (isDragging) {
      // Create preview element for drag
      const previewElement = createElementFromBounds(
        this.state.mode,
        startPoint.x,
        startPoint.y,
        snappedX,
        snappedY,
        0 // No minimum size for preview
      );
      
      this.state = {
        ...this.state,
        previewElement,
      };
      
      // Update preview visualization
      this.updatePreviewNode();
    } else {
      // If not dragging yet, clear preview (will show default size on click)
      this.clearPreview();
    }
  }

  /**
   * Finish creating an element (mouse up)
   */
  finishCreating(): EditableElement | null {
    if (!this.state.isCreating || !this.state.startPoint || !this.state.currentPoint) {
      return null;
    }
    
    const { startPoint, currentPoint, mode } = this.state;
    
    // Calculate distance to determine if it's a click or drag
    const deltaX = Math.abs(currentPoint.x - startPoint.x);
    const deltaY = Math.abs(currentPoint.y - startPoint.y);
    const isDrag = deltaX > 5 || deltaY > 5;
    
    let newElement: EditableElement | null = null;
    
    if (isDrag) {
      // Create element from drag bounds
      newElement = createElementFromBounds(
        mode,
        startPoint.x,
        startPoint.y,
        currentPoint.x,
        currentPoint.y,
        this.options.minSize
      );
    } else {
      // Create element at click point with default size
      newElement = createElementAtPoint(mode, startPoint.x, startPoint.y);
    }
    
    // Notify callback if element was created
    if (newElement && this.onElementCreated) {
      this.onElementCreated(newElement);
    }
    
    // Reset creation state and auto-switch to select mode
    this.state = {
      mode: { type: 'select' },
      isCreating: false,
      startPoint: null,
      currentPoint: null,
      previewElement: null,
    };
    
    this.clearPreview();
    
    return newElement;
  }

  /**
   * Cancel creation (Escape key)
   */
  cancelCreating(): void {
    this.state = {
      ...this.state,
      isCreating: false,
      startPoint: null,
      currentPoint: null,
      previewElement: null,
    };
    this.clearPreview();
  }

  /**
   * Create element immediately at specified center point
   */
  createAtCenter(centerX: number, centerY: number, mode: CreateMode): EditableElement | null {
    if (mode.type === 'select' || mode.type === 'pan') return null;
    
    const newElement = createElementAtPoint(mode, centerX, centerY);
    
    if (newElement && this.onElementCreated) {
      this.onElementCreated(newElement);
    }
    
    // Auto-switch to select mode after creation
    this.state = {
      ...this.state,
      mode: { type: 'select' },
    };
    
    this.hideCursorPreview();
    
    return newElement;
  }

  /**
   * Create element at point (for click-to-create)
   */
  createAtPoint(x: number, y: number): EditableElement | null {
    if (!this.isCreateMode() || this.isCreating()) return null;
    
    const snappedX = this.snapToGridValue(x);
    const snappedY = this.snapToGridValue(y);
    
    const newElement = createElementAtPoint(this.state.mode, snappedX, snappedY);
    
    if (newElement && this.onElementCreated) {
      this.onElementCreated(newElement);
    }
    
    // Auto-switch to select mode after creation
    this.state = {
      mode: { type: 'select' },
      isCreating: false,
      startPoint: null,
      currentPoint: null,
      previewElement: null,
    };
    
    this.hideCursorPreview();
    
    return newElement;
  }

  /**
   * Update preview node visualization
   */
  private updatePreviewNode(): void {
    this.clearPreview();
    
    if (!this.state.previewElement) return;
    
    const { type, x, y, width, height, data } = this.state.previewElement;
    
    this.previewNode = new Konva.Group({
      x,
      y,
      opacity: 0.6,
    });
    
    switch (type) {
      case 'sticky':
        this.createStickyPreview(width, height, data);
        break;
      case 'text':
        this.createTextPreview(width, height, data);
        break;
      case 'shape':
        this.createShapePreview(width, height, data);
        break;
      case 'organizer':
        this.createOrganizerPreview(width, height);
        break;
      case 'section':
        this.createSectionPreview(width, height);
        break;
    }
    
    if (this.previewNode.hasChildren()) {
      this.layer.add(this.previewNode);
      this.previewNode.moveToTop();
      this.layer.batchDraw();
    }
  }

  /**
   * Create sticky note preview
   */
  private createStickyPreview(width: number, height: number, data: any): void {
    if (!this.previewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: data?.backgroundColor || '#FFF9C4',
      cornerRadius: 4,
      stroke: '#0066FF',
      strokeWidth: 2,
      dash: [5, 5],
    });
    this.previewNode.add(rect);
  }

  /**
   * Create text preview
   */
  private createTextPreview(width: number, height: number, data: any): void {
    if (!this.previewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: 'transparent',
      stroke: '#0066FF',
      strokeWidth: 2,
      dash: [5, 5],
    });
    this.previewNode.add(rect);
  }

  /**
   * Create shape preview
   */
  private createShapePreview(width: number, height: number, data: any): void {
    if (!this.previewNode) return;
    
    const shapeType = data?.shapeType || 'rectangle-rounded';
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2;
    const cornerRadius = 12; // Default 12px for rounded shapes
    
    const baseProps = {
      fill: data?.fill || '#2196F3',
      stroke: '#0066FF',
      strokeWidth: 2,
      dash: [5, 5],
    };
    
    let shape: Konva.Shape | Konva.Group;
    
    switch (shapeType) {
      case 'rectangle-rounded':
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius,
          ...baseProps,
        });
        break;
      case 'rectangle-pill':
        const pillRadius = Math.min(width, height) / 2;
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius: pillRadius,
          ...baseProps,
        });
        break;
      case 'circle':
        shape = new Konva.Circle({
          x: centerX,
          y: centerY,
          radius,
          ...baseProps,
        });
        break;
      case 'triangle-up':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 3,
          radius,
          rotation: -90,
          ...baseProps,
        });
        break;
      case 'triangle-down':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 3,
          radius,
          rotation: 90,
          ...baseProps,
        });
        break;
      case 'diamond':
        // Use regular polygon for preview (rounded corners not shown in preview)
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 4,
          radius,
          rotation: 45,
          ...baseProps,
        });
        break;
      case 'line-curved-arrow':
        // Simple curved line preview
        const group1 = new Konva.Group();
        const curvedLine = new Konva.Shape({
          stroke: '#0066FF',
          strokeWidth: 2,
          dash: [5, 5],
          sceneFunc: (context, shape) => {
            const ctx = context;
            ctx.beginPath();
            ctx.moveTo(0, centerY);
            ctx.bezierCurveTo(
              width * 0.3, centerY - height * 0.3,
              width * 0.7, centerY + height * 0.3,
              width, centerY
            );
            ctx.strokeShape(shape);
          },
        });
        group1.add(curvedLine);
        shape = group1;
        break;
      case 'line-parallel':
        const group2 = new Konva.Group();
        const spacing = Math.min(10, height * 0.2);
        const line1 = new Konva.Line({
          points: [0, centerY - spacing / 2, width, centerY - spacing / 2],
          stroke: '#0066FF',
          strokeWidth: 2,
          dash: [5, 5],
          lineCap: 'round',
        });
        const line2 = new Konva.Line({
          points: [0, centerY + spacing / 2, width, centerY + spacing / 2],
          stroke: '#0066FF',
          strokeWidth: 2,
          dash: [5, 5],
          lineCap: 'round',
        });
        group2.add(line1);
        group2.add(line2);
        shape = group2;
        break;
      default:
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius,
          ...baseProps,
        });
        break;
    }
    
    this.previewNode.add(shape);
  }

  /**
   * Create organizer preview
   */
  private createOrganizerPreview(width: number, height: number): void {
    if (!this.previewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: '#FFFBF0',
      stroke: '#FFC470',
      strokeWidth: 2,
      cornerRadius: 8,
      dash: [5, 5],
    });
    this.previewNode.add(rect);
  }

  /**
   * Create section preview
   */
  private createSectionPreview(width: number, height: number): void {
    if (!this.previewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: '#FFFFFF',
      stroke: '#E0E0E0',
      strokeWidth: 1,
      cornerRadius: 8,
      dash: [5, 5],
    });
    this.previewNode.add(rect);
  }

  /**
   * Clear preview node
   */
  private clearPreview(): void {
    if (this.previewNode) {
      this.previewNode.destroy();
      this.previewNode = null;
      this.layer.batchDraw();
    }
  }

  /**
   * Create cursor preview for sticky note (semi-transparent)
   */
  private createCursorStickyPreview(width: number, height: number, data: any): void {
    if (!this.cursorPreviewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: data?.backgroundColor || '#FFF9C4',
      cornerRadius: 4,
      stroke: '#333',
      strokeWidth: 1,
    });
    this.cursorPreviewNode.add(rect);
  }

  /**
   * Create cursor preview for text (semi-transparent)
   */
  private createCursorTextPreview(width: number, height: number, data: any): void {
    if (!this.cursorPreviewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: data?.fill || '#FFFFFF',
      stroke: '#333',
      strokeWidth: 1,
    });
    this.cursorPreviewNode.add(rect);
  }

  /**
   * Create cursor preview for shape (semi-transparent)
   */
  private createCursorShapePreview(width: number, height: number, data: any): void {
    if (!this.cursorPreviewNode) return;
    
    const shapeType = data?.shapeType || 'rectangle-rounded';
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2;
    const cornerRadius = 12;
    
    const baseProps = {
      fill: data?.fill || '#2196F3',
      stroke: '#333',
      strokeWidth: 1,
    };
    
    let shape: Konva.Shape | Konva.Group;
    
    switch (shapeType) {
      case 'rectangle-rounded':
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius,
          ...baseProps,
        });
        break;
      case 'rectangle-pill':
        const pillRadius = Math.min(width, height) / 2;
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius: pillRadius,
          ...baseProps,
        });
        break;
      case 'circle':
        shape = new Konva.Circle({
          x: centerX,
          y: centerY,
          radius,
          ...baseProps,
        });
        break;
      case 'triangle-up':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 3,
          radius,
          rotation: -90,
          ...baseProps,
        });
        break;
      case 'triangle-down':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 3,
          radius,
          rotation: 90,
          ...baseProps,
        });
        break;
      case 'diamond':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 4,
          radius,
          rotation: 45,
          ...baseProps,
        });
        break;
      case 'line-curved-arrow':
        const group1 = new Konva.Group();
        const curvedLine = new Konva.Shape({
          stroke: baseProps.fill,
          strokeWidth: 2,
          sceneFunc: (context, shape) => {
            const ctx = context;
            ctx.beginPath();
            ctx.moveTo(0, centerY);
            ctx.bezierCurveTo(
              width * 0.3, centerY - height * 0.3,
              width * 0.7, centerY + height * 0.3,
              width, centerY
            );
            ctx.strokeShape(shape);
          },
        });
        group1.add(curvedLine);
        shape = group1;
        break;
      case 'line-parallel':
        const group2 = new Konva.Group();
        const spacing = Math.min(10, height * 0.2);
        const line1 = new Konva.Line({
          points: [0, centerY - spacing / 2, width, centerY - spacing / 2],
          stroke: baseProps.fill,
          strokeWidth: 2,
          lineCap: 'round',
        });
        const line2 = new Konva.Line({
          points: [0, centerY + spacing / 2, width, centerY + spacing / 2],
          stroke: baseProps.fill,
          strokeWidth: 2,
          lineCap: 'round',
        });
        group2.add(line1);
        group2.add(line2);
        shape = group2;
        break;
      default:
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius,
          ...baseProps,
        });
        break;
    }
    
    this.cursorPreviewNode.add(shape);
  }

  /**
   * Create cursor preview for organizer (semi-transparent)
   */
  private createCursorOrganizerPreview(width: number, height: number): void {
    if (!this.cursorPreviewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: '#FFFBF0',
      stroke: '#333',
      strokeWidth: 1,
      cornerRadius: 8,
    });
    this.cursorPreviewNode.add(rect);
  }

  /**
   * Create cursor preview for section (semi-transparent)
   */
  private createCursorSectionPreview(width: number, height: number): void {
    if (!this.cursorPreviewNode) return;
    
    const rect = new Konva.Rect({
      width,
      height,
      fill: '#FFFFFF',
      stroke: '#333',
      strokeWidth: 1,
      cornerRadius: 8,
    });
    this.cursorPreviewNode.add(rect);
  }

  /**
   * Get preview element
   */
  getPreviewElement(): EditableElement | null {
    return this.state.previewElement;
  }

  /**
   * Get cursor for current mode
   */
  getCursor(): string {
    if (this.state.mode.type === 'pan') return 'grab';
    if (this.state.isCreating) return 'crosshair';
    if (this.isCreateMode()) return 'crosshair';
    return 'default';
  }

  /**
   * Show cursor preview when tool is selected
   */
  showCursorPreview(x: number, y: number): void {
    if (!this.isCreateMode() || this.isCreating()) return;
    
    // Hide existing preview if any
    this.hideCursorPreview();
    
    // Get default element size for preview
    const defaultElement = createElementAtPoint(this.state.mode, 0, 0);
    if (!defaultElement) return;
    
    const { type, width, height, data } = defaultElement;
    
    // Create cursor preview node
    this.cursorPreviewNode = new Konva.Group({
      x,
      y,
      opacity: 0.5, // Semi-transparent
    });
    
    // Create preview based on element type
    switch (type) {
      case 'sticky':
        this.createCursorStickyPreview(width, height, data);
        break;
      case 'text':
        this.createCursorTextPreview(width, height, data);
        break;
      case 'shape':
        this.createCursorShapePreview(width, height, data);
        break;
      case 'organizer':
        this.createCursorOrganizerPreview(width, height);
        break;
      case 'section':
        this.createCursorSectionPreview(width, height);
        break;
    }
    
    if (this.cursorPreviewNode.hasChildren()) {
      this.layer.add(this.cursorPreviewNode);
      this.cursorPreviewNode.moveToTop();
      this.layer.batchDraw();
    }
  }

  /**
   * Update cursor preview position
   */
  updateCursorPreviewPosition(x: number, y: number): void {
    if (!this.cursorPreviewNode || !this.isCreateMode() || this.isCreating()) {
      // Show preview if it doesn't exist and we're in create mode
      if (this.isCreateMode() && !this.isCreating()) {
        this.showCursorPreview(x, y);
      }
      return;
    }
    
    const snappedX = this.snapToGridValue(x);
    const snappedY = this.snapToGridValue(y);
    
    this.cursorPreviewNode.position({ x: snappedX, y: snappedY });
    this.layer.batchDraw();
  }

  /**
   * Hide cursor preview
   */
  hideCursorPreview(): void {
    if (this.cursorPreviewNode) {
      this.cursorPreviewNode.destroy();
      this.cursorPreviewNode = null;
      this.layer.batchDraw();
    }
  }

  /**
   * Destroy the creation manager
   */
  destroy(): void {
    this.clearPreview();
    this.hideCursorPreview();
    this.onElementCreated = null;
  }
}
